import { SEOProps } from '@/components/seo/SEO';
import { SCHEMA_TEMPLATES, SEO_CONFIG } from '@/constants/seo';
import type { RestaurantWithProduct } from '@/types';

// 매장 상세 페이지용 동적 SEO 생성
export function generateStoreSEO(store: RestaurantWithProduct): SEOProps {
  const discount = 40; // 기본 할인율
  const location = store.address || '서울';
  const category = store.store_type || '음식점';
  
  // 메뉴 아이템들 추출 (상품명들)
  const menuItems = store.products?.slice(0, 3).map(p => p.name) || [];
  const menuText = menuItems.length > 0 ? menuItems.join(', ') : store.bestmenu || '다양한 메뉴';
  
  const title = `${store.name} ${discount}% 마감할인 | ${category} | ${location} | 에코딜`;
  const description = `${store.name} 오늘 마감할인 ${discount}%! ${menuText} 할인 중. ${location}에서 픽업 가능. 영업시간 확인 필요`;
  
  const keywords = [
    store.name,
    `${store.name} 할인`,
    `${store.name} 마감할인`,
    category,
    `${location} ${category}`,
    `${location} 맛집 할인`,
    ...menuItems.map(item => `${item} 할인`),
    '마감할인',
    '음식할인',
    `${discount}% 할인`,
    location,
  ];
  
  const structuredData = SCHEMA_TEMPLATES.LOCAL_BUSINESS({
    name: store.name,
    city: '서울',
    region: '서울특별시',
    street_address: store.address || '',
    cuisine_type: category,
    menu_url: `${SEO_CONFIG.SITE_URL}/store/${store.id}`,
  });

  return {
    title,
    description,
    keywords,
    ogTitle: `${store.name} - 마감할인 ${discount}%`,
    ogDescription: `${store.name}에서 ${discount}% 할인! ${menuText}`,
    ogUrl: `${SEO_CONFIG.SITE_URL}/store/${store.id}`,
    structuredData,
    canonical: `${SEO_CONFIG.SITE_URL}/store/${store.id}`,
  };
}

// 상품 상세 페이지용 동적 SEO 생성
export function generateProductSEO(
  product: any, 
  store: RestaurantWithProduct
): SEOProps {
  const discount = product.discount_rate || 40;
  const originalPrice = product.original_price;
  const discountedPrice = product.discounted_price;
  const location = store.address || '서울';
  
  const title = `${product.name} ${discount}% 할인 | ${store.name} | 에코딜`;
  const description = `${product.name} 지금 ${discount}% 할인! 원가 ${originalPrice?.toLocaleString()}원 → ${discountedPrice?.toLocaleString()}원. ${location}에서 수령`;
  
  const keywords = [
    product.name,
    `${product.name} 할인`,
    store.name,
    `${store.name} ${product.name}`,
    `${discount}% 할인`,
    '마감할인',
    '음식할인',
    location,
    `${location} 음식할인`,
  ];
  
  const structuredData = SCHEMA_TEMPLATES.PRODUCT(product, store);

  return {
    title,
    description,
    keywords,
    ogTitle: `${product.name} - ${discount}% 할인`,
    ogDescription: `${product.name} ${discount}% 할인! ${originalPrice?.toLocaleString()}원 → ${discountedPrice?.toLocaleString()}원`,
    ogUrl: `${SEO_CONFIG.SITE_URL}/store/${store.id}/product/${product.id}`,
    structuredData,
    canonical: `${SEO_CONFIG.SITE_URL}/store/${store.id}/product/${product.id}`,
  };
}

// 검색 페이지용 동적 SEO 생성
export function generateSearchSEO(query?: string, location?: string): SEOProps {
  if (!query && !location) {
    return {
      title: '음식 할인 검색 | 에코딜',
      description: '마감할인, 예약할인 음식을 검색하세요. 40-50% 할인된 음식을 지역별로 찾아보세요.',
      keywords: ['음식검색', '할인검색', '마감할인 검색', '지역별 음식할인'],
    };
  }
  
  const searchTerms = [];
  if (query) searchTerms.push(query);
  if (location) searchTerms.push(location);
  
  const searchText = searchTerms.join(' ');
  const title = `${searchText} 할인 검색 결과 | 에코딜`;
  const description = `${searchText} 마감할인, 예약할인 음식을 찾았습니다. 40-50% 할인된 음식을 확인해보세요.`;
  
  const keywords = [
    searchText,
    `${searchText} 할인`,
    `${searchText} 마감할인`,
    '음식검색',
    '할인검색',
  ];

  return {
    title,
    description,
    keywords,
    ogTitle: `${searchText} 할인 음식`,
    ogDescription: `${searchText} 마감할인 음식을 40-50% 할인가에 만나보세요`,
  };
}

// 지역별 SEO 생성
export function generateLocationSEO(location: string): SEOProps {
  const title = `${location} 마감할인 음식 | 40-50% 할인 | 에코딜`;
  const description = `${location} 지역 마감할인 음식점을 찾아보세요. 김밥, 도시락, 샐러드, 베이커리 등 40-50% 할인된 음식을 픽업하세요.`;
  
  const keywords = [
    `${location} 마감할인`,
    `${location} 음식할인`,
    `${location} 도시락 할인`,
    `${location} 김밥 할인`,
    `${location} 맛집 할인`,
    `${location} 베이커리 할인`,
    `${location} 카페 할인`,
  ];

  return {
    title,
    description,
    keywords,
    ogTitle: `${location} 마감할인 40-50%`,
    ogDescription: `${location} 지역 마감할인 음식을 40-50% 할인가에 만나보세요`,
  };
}

// 카테고리별 SEO 생성
export function generateCategorySEO(category: string): SEOProps {
  const title = `${category} 마감할인 | 40-50% 할인 | 에코딜`;
  const description = `${category} 마감할인을 40-50% 할인된 가격에 구매하세요. 신선한 ${category}를 저렴하게 만나보세요.`;
  
  const keywords = [
    `${category} 할인`,
    `${category} 마감할인`,
    `${category} 세일`,
    `${category} 떨이`,
    `저렴한 ${category}`,
    `${category} 40% 할인`,
    `${category} 50% 할인`,
  ];

  return {
    title,
    description,
    keywords,
    ogTitle: `${category} 마감할인 40-50%`,
    ogDescription: `신선한 ${category}를 40-50% 할인된 가격에 만나보세요`,
  };
}